import React, { useCallback, useEffect, useMemo, useState } from "react";
/**
 * InventoryEmbedPage - Presentation Layer
 *
 * Full-page inventory management interface for C-Market embed access via JWT token.
 *
 * Flow:
 * 1. Verify JWT token from URL using shared useEmbedAuth hook
 * 2. Render inventory management UI (UserView)
 *
 * URL: /embed/inventory?token=JWT
 *
 * Clean Architecture:
 * - Uses shared useEmbedAuth hook for token verification
 * - Uses shared EmbedAuthStates for consistent UI
 * - No direct VendorAuthContext dependency (prevents infinite loops)
 */

import { useSearchParams, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useVendorAuth } from "../features/auth";
import { ItemsProvider } from "../features/inventory/items";
import { UserProductManagement } from "../features/inventory/components/UserProductManagement";
import type { Product } from "@semo/shared-product";
import type {
  CategoryTreeNodeDto,
  QuoteRequestListItemDto,
  SupplierSettlementResponseDto,
  ContractListResponseDto,
} from "@semo/api-contracts";
import { RespondedProjectList } from "../features/quote-requests/presentation/components/RespondedProjectList";
import { ContractList } from "../features/contracts/components/ContractList";
import { MallShipmentList } from "../features/mall-shipments";
import { ChatRoomList } from "../features/chat";
import { ChatInboxPreview } from "./DevChatListPreview";
import { SettlementList } from "../features/settlements";
import { MyProfileChip } from "../features/profile/components/MyProfileChip";
import { Switch, Dialog, DialogContent, DialogTitle } from "@semo/shared-ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@semo/shared-ui";
import { toast } from "sonner";
import {
  Package,
  Briefcase,
  FileText,
  DollarSign,
  MessageSquare,
  MessageCircle,
  Search as SearchIcon,
  Bell,
  // Calculator,
  // 원가계산서 탭 숨김과 함께 봉인 — 재노출 시 같이 푼다
  Truck,
} from "@semo/icons";
import {
  useExternalSuppliersControllerUpdateSupplierMe,
  type UpdateSupplierMeDto,
  useExternalEmbedControllerGetEmbedUrl,
  useChatControllerGetRooms,
  useExternalQuoteRequestsControllerFindOne,
  getExternalQuoteRequestsControllerFindOneQueryKey,
  externalQuoteRequestsControllerFindOne,
  useNotificationsControllerGetUnreadCount,
} from "@semo/api-contracts";
import { VendorChatWindow } from "../components/VendorChatWindow";
import { QuoteRequestListPage } from "../features/quote-requests";
import { CostEstimateView } from "../features/cost-estimate/CostEstimateView";
import { EmbedService } from "../features/quote-requests/application/services/EmbedService";
import { BaseQuoteModal } from "../components/modals/BaseQuoteModal";
import {
  isEmbeddedInIframe,
  notifyParentEmbedClose,
} from "../utils/embedMessaging";
import {
  useQuoteSubmission,
  type QuoteItem,
  type QuoteFormFile,
  type QuoteVatMode,
} from "../features/quotes";
import { useSupplierPrices } from "../features/quotes/presentation/hooks/useSupplierPrices";
import { RespondedProjectDetailPage } from "../features/quote-requests/presentation/pages/RespondedProjectDetailPage";
import { Badge, HeaderActionButton } from "@semo/shared-ui";
import { VendorNotificationDropdown } from "../features/notifications/components/VendorNotificationDropdown";

// Local interface extension until client is regenerated
interface ExtendedSupplierInfo {
  autoQuoteEnabled?: boolean;
  kakaoNotificationEnabled?: boolean;
  companyName: string;
}

interface InventoryEmbedPageProps {
  initialTab?: string;
  /** DEV 전용: 상품/단가 관리 탭을 샘플 데이터로 미리보기 (/dev/preview?tab=products). */
  previewProducts?: Product[];
  previewCategoryTree?: CategoryTreeNodeDto[];
  /** DEV 전용: 프로젝트 찾기 탭을 샘플 공고로 미리보기 (/dev/preview?tab=all-projects). */
  previewProjects?: QuoteRequestListItemDto[];
  /** DEV 전용: 참여 프로젝트 탭을 샘플(참여 완료) 공고로 미리보기 (/dev/preview?tab=projects). */
  previewRespondedProjects?: QuoteRequestListItemDto[];
  /** DEV 전용: 채팅 탭을 마스터-디테일 목업(더미)으로 미리보기 (/dev/preview?tab=chat-rooms). */
  previewChat?: boolean;
  /** DEV 전용: 수수료 정산 탭을 샘플 데이터로 미리보기 (/dev/preview?tab=settlements). */
  previewSettlements?: SupplierSettlementResponseDto[];
  /** DEV 전용: 계약/주문 내역 탭을 샘플 데이터로 미리보기 (/dev/preview?tab=contracts). */
  previewContracts?: ContractListResponseDto[];
}

/** 보라 머리줄의 채팅·알림 단추 — 바탕·테두리를 비우고 눌렀을 때만 살짝 밝힌다 */
const HEADER_ICON_BUTTON =
  "bg-transparent border-transparent hover:bg-white/15 hover:border-transparent hover:translate-y-0";

export const InventoryEmbedPage: React.FC<InventoryEmbedPageProps> = ({
  initialTab,
  previewProducts,
  previewCategoryTree,
  previewProjects,
  previewRespondedProjects,
  previewChat,
  previewSettlements,
  previewContracts,
}) => {
  const queryClient = useQueryClient();
  const { supplierInfo, currentUser } = useVendorAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { contractId, requestId } = useParams<{
    contractId: string;
    requestId: string;
  }>();
  const [autoQuoteEnabled, setAutoQuoteEnabled] = useState<boolean>(false);
  const [kakaoNotificationEnabled, setKakaoNotificationEnabled] =
    useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedChatRoomId, setSelectedChatRoomId] = useState<string | null>(
    null,
  );
  const [selectedQuoteRequestId, setSelectedQuoteRequestId] = useState<
    string | null
  >(null);
  const [selectedDetailRequestId, setSelectedDetailRequestId] = useState<
    string | null
  >(null);
  // 2026-07-31: /embed/quote-detail/:requestId 진입 여부. true 인 동안 상세 모달의
  //   발주처 정보는 노출하고, 내부 액션(1:1 채팅) 은 숨긴다. 모달을 닫으면 리셋.
  const [detailFromEmbed, setDetailFromEmbed] = useState<boolean>(false);

  useEffect(() => {
    if (requestId) {
      setSelectedDetailRequestId(requestId);
      setDetailFromEmbed(true);
    }
  }, [requestId]);

  // Pre-fetch chat rooms for linking
  const { data: chatRoomsData } = useChatControllerGetRooms({
    page: 1,
    limit: 100,
  });

  const embedUrlMutation = useExternalEmbedControllerGetEmbedUrl();

  // Handle chat opening from quote request page
  const handleChatOpen = async (chatRoomIdOrRequestId: string) => {
    // 1. Try to find if it's already a chat room
    const rooms = chatRoomsData?.data?.items || [];
    const directRoom = rooms.find((r) => r.id === chatRoomIdOrRequestId);
    const linkedRoom = rooms.find(
      (r) => r.quoteRequestId === chatRoomIdOrRequestId,
    );

    const roomId = directRoom?.id || linkedRoom?.id;

    if (roomId) {
      // Open in local modal if found
      setSelectedChatRoomId(roomId);
      return;
    }

    // 2. Fallback to external popup if not found (might be a new request without chat yet)
    try {
      const response = await embedUrlMutation.mutateAsync({
        data: {
          type: "chat",
          targetId: chatRoomIdOrRequestId,
          origin: window.location.origin,
        },
      });

      EmbedService.handleEmbedUrlResponse(response);
    } catch (error) {
      EmbedService.handleEmbedUrlError(error);
    }
  };

  const handleRespondOpen = useCallback(
    async (requestId: string) => {
      try {
        // Pre-fetch data to show loading state on button
        await queryClient.fetchQuery({
          queryKey:
            getExternalQuoteRequestsControllerFindOneQueryKey(requestId),
          queryFn: ({ signal }) =>
            externalQuoteRequestsControllerFindOne(requestId, signal),
        });
        setSelectedQuoteRequestId(requestId);
      } catch (error) {
        console.error("Failed to load quote request details:", error);
        toast.error("견적 요청 정보를 불러오는데 실패했습니다.");
      }
    },
    [queryClient],
  );

  const handleProjectClick = useCallback((requestId: string) => {
    setSelectedDetailRequestId(requestId);
  }, []);

  // Use external supplierId (C-Market ID) if available, otherwise fallback to UUID
  const supplierId = supplierInfo?.supplierId || currentUser?.id;

  // Fetch quote request data for modal
  const { data: quoteRequestData, isLoading: requestLoading } =
    useExternalQuoteRequestsControllerFindOne(selectedQuoteRequestId || "", {
      query: {
        enabled: !!selectedQuoteRequestId,
      },
    });

  // Batch fetch supplier prices for all quote items
  const { priceMap, isLoading: pricesLoading } = useSupplierPrices(
    (quoteRequestData?.data?.items || []).map((item) => ({
      itemId: item.itemId,
    })),
    supplierId ?? undefined,
  );

  // Use presentation hook for submission
  const { handleSubmit: submitQuote, isSubmitting } = useQuoteSubmission(
    selectedQuoteRequestId || "",
  );

  const handleQuoteSubmit = async (
    items: QuoteItem[],
    notes: string,
    files: QuoteFormFile[],
    vatMode: QuoteVatMode,
  ) => {
    if (!selectedQuoteRequestId) return;

    // Error handling is managed by BaseQuoteModal (logs) and ApplicationService (toasts)
    await submitQuote(items, notes, files, vatMode);
    setSelectedQuoteRequestId(null);
  };

  // Default to 'all-projects' tab
  const activeTab = searchParams.get("tab") || initialTab || "all-projects";

  const handleTabChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", value);
    setSearchParams(newParams, { replace: true });
  };

  // DEV preview 전용: 다른 탭 "채팅하기" → 채팅 탭(마스터-디테일 목업)으로 이동 + 방 선택
  const [previewChatTargetId, setPreviewChatTargetId] = useState<string | null>(
    null,
  );
  const handlePreviewOpenChat = (id: string) => {
    setPreviewChatTargetId(id);
    handleTabChange("chat-rooms");
  };

  // Orval mutation hook
  const { mutateAsync: updateSupplierSettings } =
    useExternalSuppliersControllerUpdateSupplierMe();

  // Initialize state from supplierInfo
  useEffect(() => {
    if (supplierInfo) {
      // Cast to extended type safely
      const info = supplierInfo as unknown as ExtendedSupplierInfo;
      setAutoQuoteEnabled(!!info.autoQuoteEnabled);
      setKakaoNotificationEnabled(info.kakaoNotificationEnabled !== false);
    }
  }, [supplierInfo]);

  const handleAutoQuoteChange = async (checked: boolean) => {
    // Optimistic update
    const previousState = autoQuoteEnabled;
    setAutoQuoteEnabled(checked);
    setIsUpdating(true);

    try {
      const updateDto: UpdateSupplierMeDto = {
        autoQuoteEnabled: checked,
      };

      await updateSupplierSettings({ data: updateDto });

      toast.success(
        checked
          ? "자동 견적 발송이 활성화되었습니다."
          : "자동 견적 발송이 비활성화되었습니다.",
      );
    } catch (err) {
      console.error("Failed to update auto-quote settings:", err);
      // Revert on error
      setAutoQuoteEnabled(previousState);
      toast.error("설정 변경에 실패했습니다.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleKakaoNotificationChange = async (checked: boolean) => {
    // Optimistic update
    const previousState = kakaoNotificationEnabled;
    setKakaoNotificationEnabled(checked);
    setIsUpdating(true);

    try {
      const updateDto: UpdateSupplierMeDto = {
        kakaoNotificationEnabled: checked,
      };

      await updateSupplierSettings({ data: updateDto });

      toast.success(
        checked
          ? "카카오 알림톡 수신이 활성화되었습니다."
          : "카카오 알림톡 수신이 비활성화되었습니다.",
      );
    } catch (err) {
      console.error("Failed to update kakao notification settings:", err);
      // Revert on error
      setKakaoNotificationEnabled(previousState);
      toast.error("설정 변경에 실패했습니다.");
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * 씨마켓 모달(iframe) 안인가. 부모 띠가 이미 세모 심볼과 닫기를 그리므로 머리줄에서
   * 심볼·이름을 뺀다. 팝업(top-level)이면 부모 띠가 없어 그대로 그린다.
   * 창이 바뀌는 값이 아니라 한 번만 잰다.
   */
  const isEmbedded = useMemo(() => {
    // DEV 미리보기 `?embedded=1` — iframe 없이도 임베드 머리를 본다(`/dev/preview`).
    if (
      import.meta.env.DEV &&
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).has("embedded")
    ) {
      return true;
    }
    return isEmbeddedInIframe();
  }, []);

  const { data: unreadCountData } = useNotificationsControllerGetUnreadCount();
  const unreadCount = unreadCountData?.data?.count ?? 0;
  const chatUnreadCount = unreadCountData?.data?.chatCount ?? 0;
  const systemUnreadCount = unreadCountData?.data?.notificationCount ?? 0;

  // Authenticated - render inventory UI
  return (
    <ItemsProvider>
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="h-viewport flex flex-col gap-0 bg-slate-50 font-sans overflow-hidden"
      >
        {/*
          머리 한 줄 — 보라 띠(#6b3ad5) 안에 심볼 + 이름 + 탭 + 우측 액션을 한 줄로 둔다(운영 main #1171·#1176 과 같은 모양).

          **씨마켓 모달 안(iframe)에서는 심볼·이름을 그리지 않는다.** 부모 모달이 같은 색 띠에 세모 심볼과
          닫기(X)를 이미 그려서 로고가 위아래로 두 번 보였다(2026-09-27 대표 지적). 팝업(top-level)으로
          열리면 부모 띠가 없으니 그대로 그린다.

          탭은 넘치면 가로 스크롤(no-scrollbar)로 두 줄 낙하를 막는다 — 표 자리를 먹지 않게(main 과 같은 방식).
        */}
        <header className="flex-shrink-0 sticky top-0 z-50 shadow-sm">
          <div className="flex h-12 items-center gap-2 bg-[#6b3ad5] px-3 text-white sm:px-4">
            {!isEmbedded && (
              <>
                <img
                  src="/brand/semo-logo-white.svg"
                  alt=""
                  aria-hidden
                  className="h-5 w-auto shrink-0 select-none"
                />
                <span className="mr-1 hidden whitespace-nowrap text-sm font-semibold tracking-tight lg:inline">
                  판매자 센터
                </span>
              </>
            )}
            {/*
              `overflow-x-auto` 는 세로도 함께 자른다. 탭 줄에 세로 여백(py-1.5)을 두어 채팅 미읽음
              배지(-top-1)가 줄 밖으로 나가 씹히지 않게 한다.
            */}
            <TabsList className="h-12 min-w-0 flex-1 justify-start overflow-x-auto flex-nowrap no-scrollbar bg-transparent px-0 py-1.5 rounded-none">
              <TabsTrigger
                value="all-projects"
                className="gap-1.5 whitespace-nowrap flex-shrink-0 rounded-lg text-white/85 hover:bg-white/15 hover:text-white data-[state=active]:bg-white data-[state=active]:text-[#6b3ad5] data-[state=active]:shadow-sm"
              >
                <SearchIcon className="w-4 h-4 hidden md:block" /> 프로젝트 찾기
              </TabsTrigger>
              <TabsTrigger
                value="projects"
                className="gap-1.5 whitespace-nowrap flex-shrink-0 rounded-lg text-white/85 hover:bg-white/15 hover:text-white data-[state=active]:bg-white data-[state=active]:text-[#6b3ad5] data-[state=active]:shadow-sm"
              >
                <Briefcase className="w-4 h-4 hidden md:block" /> 참여 프로젝트
              </TabsTrigger>
              <TabsTrigger
                value="chat-rooms"
                className="gap-1.5 whitespace-nowrap flex-shrink-0 rounded-lg text-white/85 hover:bg-white/15 hover:text-white data-[state=active]:bg-white data-[state=active]:text-[#6b3ad5] data-[state=active]:shadow-sm relative"
              >
                <MessageSquare className="w-4 h-4 hidden md:block" /> 채팅 목록
                {chatUnreadCount > 0 && (
                  <Badge
                    variant="destructive"
                    className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 flex items-center justify-center text-2xs rounded-full"
                  >
                    {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="settlements"
                className="gap-1.5 whitespace-nowrap flex-shrink-0 rounded-lg text-white/85 hover:bg-white/15 hover:text-white data-[state=active]:bg-white data-[state=active]:text-[#6b3ad5] data-[state=active]:shadow-sm"
              >
                <DollarSign className="w-4 h-4 hidden md:block" /> 수수료 정산
              </TabsTrigger>
              <TabsTrigger
                value="contracts"
                className="gap-1.5 whitespace-nowrap flex-shrink-0 rounded-lg text-white/85 hover:bg-white/15 hover:text-white data-[state=active]:bg-white data-[state=active]:text-[#6b3ad5] data-[state=active]:shadow-sm"
              >
                <FileText className="w-4 h-4 hidden md:block" /> 계약/주문 내역
              </TabsTrigger>
              <TabsTrigger
                value="products"
                className="gap-1.5 whitespace-nowrap flex-shrink-0 rounded-lg text-white/85 hover:bg-white/15 hover:text-white data-[state=active]:bg-white data-[state=active]:text-[#6b3ad5] data-[state=active]:shadow-sm"
              >
                <Package className="w-4 h-4 hidden md:block" /> 상품/단가 관리
              </TabsTrigger>
              {/* 몰 배송 — 자체몰 주문의 송장을 거는 자리. 계약이 선 뒤부터 배송이 끝나기
                전까지의 주문만 나온다(다 보낸 주문은 서버가 목록에서 뺀다). */}
              <TabsTrigger
                value="shipments"
                className="gap-1.5 whitespace-nowrap flex-shrink-0 rounded-lg text-white/85 hover:bg-white/15 hover:text-white data-[state=active]:bg-white data-[state=active]:text-[#6b3ad5] data-[state=active]:shadow-sm"
              >
                <Truck className="w-4 h-4 hidden md:block" /> 몰 배송
              </TabsTrigger>
              {/* 원가계산서 탭 — 기업(회원) 노출 일단 중단(2026-08-30 사용자 지시).
                컨텐츠(CostEstimateView)와 ?tab=cost-estimate 직접 접근은 유지 — 재노출은
                이 주석만 풀면 된다. */}
              {/* <TabsTrigger
              value="cost-estimate"
              className="gap-1.5 whitespace-nowrap flex-shrink-0 rounded-lg text-white/85 hover:bg-white/15 hover:text-white data-[state=active]:bg-white data-[state=active]:text-[#6b3ad5] data-[state=active]:shadow-sm"
            >
              <Calculator className="w-4 h-4 hidden md:block" /> 원가계산서
            </TabsTrigger> */}
            </TabsList>
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {/* 채팅·알림 단추는 보라 머리줄 위에 흰 아이콘만 둔다(상자·테두리 없이) — main 과 같음. */}
              <HeaderActionButton
                icon={
                  <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                }
                badgeCount={chatUnreadCount}
                aria-label="채팅 알림"
                onClick={() => handleTabChange("chat-rooms")}
                className={HEADER_ICON_BUTTON}
              />
              <VendorNotificationDropdown
                onTabChange={handleTabChange}
                triggerClassName={HEADER_ICON_BUTTON}
                iconClassName="text-white"
              />
              <MyProfileChip companyName={supplierInfo?.companyName || ""} />
            </div>
          </div>
        </header>

        <main
          className={`flex-1 flex flex-col w-full min-h-0 relative ${
            activeTab === "products" ? "" : "p-4 md:p-6"
          }`}
        >
          <TabsContent
            value="all-projects"
            className="mt-0 flex-1 min-h-0 overflow-auto"
          >
            <QuoteRequestListPage
              onProjectClick={handleProjectClick}
              onChatOpen={
                previewChat
                  ? async (id) => handlePreviewOpenChat(id)
                  : handleChatOpen
              }
              onRespondOpen={handleRespondOpen}
              previewProjects={previewProjects}
            />
          </TabsContent>

          <TabsContent
            value="projects"
            className="mt-0 flex-1 min-h-0 overflow-auto"
          >
            <RespondedProjectList
              onOpenChat={
                previewChat ? handlePreviewOpenChat : setSelectedChatRoomId
              }
              previewProjects={previewRespondedProjects}
            />
          </TabsContent>

          <TabsContent
            value="chat-rooms"
            className={
              previewChat
                ? "mt-0 flex-1 min-h-0"
                : "mt-0 flex-1 min-h-0 overflow-auto"
            }
          >
            {previewChat ? (
              <ChatInboxPreview targetId={previewChatTargetId} />
            ) : (
              <ChatRoomList onOpenChat={setSelectedChatRoomId} />
            )}
          </TabsContent>

          <TabsContent
            value="shipments"
            className="mt-0 flex-1 min-h-0 overflow-auto"
          >
            <MallShipmentList />
          </TabsContent>

          <TabsContent
            value="settlements"
            className="mt-0 flex-1 min-h-0 overflow-auto"
          >
            <SettlementList previewData={previewSettlements} />
          </TabsContent>

          <TabsContent
            value="contracts"
            className="mt-0 flex-1 min-h-0 overflow-auto"
          >
            <ContractList
              initialSelectedId={contractId}
              previewData={previewContracts}
            />
          </TabsContent>

          <TabsContent value="products" className="mt-0 flex-1 min-h-0 h-full">
            <UserProductManagement
              autoQuoteEnabled={autoQuoteEnabled}
              onToggleAutoQuote={handleAutoQuoteChange}
              kakaoNotificationEnabled={kakaoNotificationEnabled}
              onToggleKakaoNotification={handleKakaoNotificationChange}
              isUpdatingSettings={isUpdating}
              previewProducts={previewProducts}
              previewCategoryTree={previewCategoryTree}
            />
          </TabsContent>

          <TabsContent
            value="cost-estimate"
            className="mt-0 flex-1 min-h-0 overflow-auto"
          >
            <CostEstimateView />
          </TabsContent>
        </main>

        {/* Global Chat Overlay */}
        {selectedChatRoomId && (
          <VendorChatWindow
            chatId={selectedChatRoomId}
            customerName={"고객"} // Will be fetched internally by VendorChatWindow
            onClose={() => setSelectedChatRoomId(null)}
            variant="modal"
            supplierId={currentUser?.id}
          />
        )}

        {/* Local Quote Response Modal
            fixed 로 «덮는» 이유: 이 자리에 그냥 두면 헤더·본문과 같은 세로줄의
            형제라, 남은 높이를 나눠 갖는다 — 모바일에서 헤더가 두 줄이 되면 그만큼
            견적서가 아래로 밀리고 하단 발송 막대가 화면 밖으로 나간다.
            (대시보드 화면은 이미 같은 모달을 덮는 층으로 띄운다.) */}
        {selectedQuoteRequestId && quoteRequestData?.data && (
          <div className="fixed inset-0 z-[100]">
            <BaseQuoteModal
              mode="create"
              variant="modal"
              quoteRequest={quoteRequestData.data}
              supplierInfo={supplierInfo ?? undefined}
              supplierId={supplierId ?? undefined}
              supplierPriceMap={priceMap}
              onClose={() => setSelectedQuoteRequestId(null)}
              onSubmit={handleQuoteSubmit}
              isSubmitting={isSubmitting || requestLoading || pricesLoading}
            />
          </div>
        )}

        {/* Project Detail Modal */}
        <Dialog
          open={!!selectedDetailRequestId}
          onOpenChange={(open) => {
            if (!open) {
              // c-market 견적서 임베드(모달)에서 열렸으면 부모 모달도 함께 닫는다.
              if (detailFromEmbed) notifyParentEmbedClose("closed");
              setSelectedDetailRequestId(null);
              setDetailFromEmbed(false);
            }
          }}
        >
          <DialogContent
            size="3xl"
            className="p-0 overflow-hidden h-[90vh]"
            aria-describedby={undefined}
            // 임베드(모달) 진입 시 내부 X 숨김 — 부모 c-market 모달의 고정 닫기로 통일.
            hideClose={detailFromEmbed}
          >
            <DialogTitle className="sr-only">프로젝트 상세</DialogTitle>
            {selectedDetailRequestId && (
              <RespondedProjectDetailPage
                requestId={selectedDetailRequestId}
                onBack={() => {
                  // 임베드(모달) 진입 상세의 닫기/뒤로 → 부모 c-market 모달도 닫는다.
                  if (detailFromEmbed) notifyParentEmbedClose("closed");
                  setSelectedDetailRequestId(null);
                  setDetailFromEmbed(false);
                }}
                onChatClick={handleChatOpen}
                hideBuyerInfo={
                  detailFromEmbed ? false : activeTab === "all-projects"
                }
                hideActions={detailFromEmbed}
              />
            )}
          </DialogContent>
        </Dialog>
      </Tabs>
    </ItemsProvider>
  );
};
